import React, { forwardRef, useEffect, useMemo, useRef } from "react";
import { useWatch } from "react-hook-form";
import { BaseSelectRef } from "rc-select/lib/BaseSelect";

import {
  BaseDropdownProps,
  Constraint,
  Option,
  SliderOption,
  WithConstraints,
  WithConstraintsProps,
} from "../types";
import { createHookFormComponent } from "../../ReactHookForm/hocs/withHookForm";

const buildParentOptionAllowedOptionsMapping = (constraints: Constraint[]) =>
  constraints.reduce<Record<number, number[]>>((acc, item) => {
    acc[item.parentOption] = item.allowedOptions;
    return acc;
  }, {});

const filterOptionsForSelectedValues = (
  selectedFieldValues: number[],
  constraints: Constraint[],
  options: Option[],
) => {
  const parentOptionAllowedOptionsMapping =
    buildParentOptionAllowedOptionsMapping(constraints);

  const allowedOptions = selectedFieldValues.reduce<(number | string)[]>(
    (acc, i) => {
      const allowedOptionsForSelectedValue =
        parentOptionAllowedOptionsMapping[i] || [];
      if (i in parentOptionAllowedOptionsMapping) {
        return [...acc, ...allowedOptionsForSelectedValue];
      }
      return acc;
    },
    [],
  );

  const newOptions = options.filter((option) =>
    allowedOptions.includes(option.value as number | string),
  );

  if (newOptions.length === 0) {
    return options;
  }

  return newOptions;
};

const filterSliderOptionsForSelectedValues = (
  selectedFieldValues: number[],
  constraints: Constraint[],
  options: SliderOption[],
) => {
  const parentOptionAllowedOptionsMapping =
    buildParentOptionAllowedOptionsMapping(constraints);

  const allowedOptions = selectedFieldValues.reduce<number[]>(
    (acc, i: number) => {
      const val = parentOptionAllowedOptionsMapping[i];
      if (val) {
        const allowedOptionsForSelectedValue = val || [];
        return [...acc, ...allowedOptionsForSelectedValue];
      }
      return acc;
    },
    [],
  );

  const hasMainConstraints = constraints.some(
    (constraint) => constraint.affectsMainOptions,
  );
  const hasSecondaryConstraints = constraints.some(
    (constraint) => !constraint.affectsMainOptions,
  );

  return options.map((option) => {
    const newOption = { ...option };

    if (hasMainConstraints) {
      newOption.disabled = !allowedOptions.includes(option.value as number);
    }

    if (hasSecondaryConstraints && option.relatedOptions?.length) {
      newOption.relatedOptions = (newOption?.relatedOptions || [])
        .map((relatedOption) => {
          if (allowedOptions.includes(relatedOption.value as number))
            return relatedOption;
          return undefined;
        })
        .filter((relatedOption) => !!relatedOption);
    }

    return newOption;
  });
};

/**
 * Filters the available options in a dropdown based on a value in a different field.
 */
export function withConstraints<T extends BaseDropdownProps>(
  WrappedComponent: React.ComponentType<T>,
): WithConstraints<T> {
  const ConstraintsComponent = forwardRef<
    BaseSelectRef,
    WithConstraintsProps<T>
  >(
    (
      {
        constraints,
        options,
        isSlider = false,
        multiple,
        onChange,
        value,
        control,
        ...restProps
      },
      ref,
    ) => {
      const watchedFieldNames = constraints
        ? Array.from(new Set(constraints.map((i) => i.fieldName.toString())))
        : [];

      const fieldValues = useWatch({
        control,
        name: watchedFieldNames,
      });

      const selectedFieldValues = useMemo(
        () =>
          ([fieldValues] as unknown[])
            .flat(2)
            .filter((item) => item != null) as number[],
        [fieldValues],
      );

      const hasConstraints = !!constraints && constraints.length > 0;

      const filteredOptions = useMemo(() => {
        if (!hasConstraints || selectedFieldValues.length === 0) {
          return options;
        }

        return isSlider
          ? filterSliderOptionsForSelectedValues(
              selectedFieldValues,
              constraints,
              options as SliderOption[],
            )
          : filterOptionsForSelectedValues(
              selectedFieldValues,
              constraints,
              options as Option[],
            );
      }, [hasConstraints, selectedFieldValues, constraints, options, isSlider]);

      const previousParentSelection = useRef<string | null>(null);
      const previousValue = useRef<string | null>(null);

      useEffect(() => {
        const parentSelection = JSON.stringify(selectedFieldValues);
        const currentValue = JSON.stringify(value ?? null);
        const previousSelection = previousParentSelection.current;
        const previousFieldValue = previousValue.current;

        previousParentSelection.current = parentSelection;
        previousValue.current = currentValue;

        if (previousSelection === null) return;
        if (previousSelection === parentSelection) return;
        if (previousFieldValue !== currentValue) return;
        if (!hasConstraints || selectedFieldValues.length === 0) return;
        if (value == null || !onChange) return;

        const currentValues = Array.isArray(value) ? value : [value];
        const validValues = currentValues.filter((val) =>
          filteredOptions.some(
            (opt) =>
              opt.value === (typeof val === "object" ? val.choice : val) ||
              typeof val === "string",
          ),
        );

        if (validValues.length === currentValues.length) return;

        onChange(multiple ? validValues : undefined);
      }, [
        selectedFieldValues,
        filteredOptions,
        hasConstraints,
        multiple,
        onChange,
        value,
      ]);

      return (
        <WrappedComponent
          {...(restProps as unknown as T)}
          control={control}
          onChange={onChange}
          value={value}
          multiple={multiple}
          options={filteredOptions}
          ref={ref}
        />
      );
    },
  );

  ConstraintsComponent.displayName = `withConstraints(${
    WrappedComponent.displayName || WrappedComponent.name || "Component"
  })`;

  return ConstraintsComponent;
}

export function createConstraintsComponent<T extends BaseDropdownProps>(
  BaseComponent: React.ComponentType<T>,
  isSlider = false,
): WithConstraints<T> {
  const ConstraintsComponent = withConstraints<T>(BaseComponent);
  return forwardRef<BaseSelectRef, WithConstraintsProps<T>>((props, ref) => {
    return <ConstraintsComponent {...props} isSlider={isSlider} ref={ref} />;
  }) as WithConstraints<T>;
}

export function createHookFormConstraintsComponent<T extends BaseDropdownProps>(
  BaseComponent: React.ComponentType<T>,
  isSlider = false,
) {
  const ConstraintsComponent = createConstraintsComponent<T>(
    BaseComponent,
    isSlider,
  );

  return createHookFormComponent<WithConstraintsProps<T>>(
    ConstraintsComponent as unknown as React.ComponentType<
      WithConstraintsProps<T>
    >,
  );
}
